import { useMotionValue } from "framer-motion";
import { useMemo, useState } from "react";

import useChartTokens from "@/common/charts/hooks/useChartTokens";
import MotionBox, { MotionBoxProps } from "@/common/components/MotionBox";

export type SimplePieChartProps = MotionBoxProps & {
  value: number;
  color?: string;
  bgColor?: string;
  size?: number | string;
  loading?: boolean;
};

export const SimplePieChart = ({
  value,
  color,
  bgColor,
  size = 3,
  loading = false,
  ...rest
}: SimplePieChartProps) => {
  const { colors } = useChartTokens();
  const foregroundColor = color ?? colors.neutral;
  const backgroundColor = bgColor ?? colors.neutralVeryLight;

  const [startedLoading] = useState(loading);

  const roundedValue = Math.round(value);
  const opacity = useMotionValue(roundedValue);

  const variants = useMemo(
    () => ({
      loading: {
        opacity: [0.4, 0.8, 0.4],
        backgroundImage: `conic-gradient(${foregroundColor} 0%,
          ${backgroundColor} 0)`,
        transition: {
          duration: 1.5,
          ease: "easeInOut",
          repeat: Infinity,
        },
      },
      done: {
        opacity: 1,
        backgroundImage: `conic-gradient(${foregroundColor} ${roundedValue}%,
          ${backgroundColor} 0)`,
        transition: {
          duration: !startedLoading ? 0 : 0.3,
          ease: "easeInOut",
        },
      },
    }),
    [foregroundColor, backgroundColor, roundedValue, startedLoading],
  );

  return (
    <>
      <MotionBox
        animate={loading ? "loading" : "done"}
        borderRadius="full"
        boxSize={size}
        flexShrink={0}
        initial="done"
        style={{ opacity }}
        variants={variants}
        {...rest}
      />
    </>
  );
};
